/* eslint-disable max-len */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { MetricCounter,
  DescriptionContainer,
  CalloutWithAutoPlacement,
  InsightAction,
  ActionSubPanel,
} from '@bingads-webui-campaign/fluctuation-common';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { instrumentationSchema } from './instrumentation-schema';
import { ActionCauseContainer } from './action-cause-container';
import { AuctionInsightLogService } from './auction-insight-log-service';

export const AuctionInsightPopup = ({
  i18n,
  legacyI18n,
  instrumentation,
  context,
  insightData,
  accountsInfo,
  onDismiss,
  triggerButton,
}) => {
  const {
    startDate, endDate, metric, domainNumber,
  } = insightData;
  const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

  const [currentSubPanel, setCurrentSubPanel] = useState(ActionSubPanel.None);
  const [isDetailDomExpanded, setIsDetailDomExpanded] = useState(false);
  const activities = useRef({});
  const logService = new AuctionInsightLogService({ instrumentation });

  useEffect(() => {
    instrumentation.addScenario(instrumentationSchema);
    const fluctuationScenario = instrumentation.auctionInsight.create();
    activities.current = {
      userActionActivity: fluctuationScenario.userAction.create(),
      feedbackActivity: fluctuationScenario.auctionInsightFeedback.create(),
    };
  }, [instrumentation]);

  const domainNumberString = (
    <strong>{i18n.getString(_TL_('{{number}} domains'), { number: domainNumber })}</strong>
  );

  const description = (
    <ReactTemplate
      model={{ domainNumber: domainNumberString }}
      template={i18n.getString(_TL_('{{domainNumber}} are outperforming you. Consider optimizing the following accounts to stay ahead of the auction'))}
    />);

  const feedbackOptions = [
    {
      key: '0',
      text: i18n.getString(_TL_('This was helpful.')),
      name: 'feedback1',
    }, {
      key: '1',
      text: i18n.getString(_TL_('I disagree with this assessment.')),
      name: 'feedback2',
    },
  ];

  const onFeedbackSubmit = (log) => {
    const feedbackData = {
      Comment: log.comment,
      Feedbacks: log.feedbacks ? log.feedbacks.map((item) => {
        if (item === '0') {
          return 'InsightIsHelpful';
        }
        return 'DisagreeTheInsight';
      }) : [],
    };
    logService.logFeedback({
      insightData,
      context,
      note: feedbackData,
    });
  };

  const onDeepLinkClick = (actionInfo) => {
    logService.logAction({
      insightData,
      context,
      note: actionInfo,
    });
  };

  return (
    <CalloutWithAutoPlacement
      calloutKey={`${dateRange}-${currentSubPanel}-${isDetailDomExpanded}`}
      trigger="click"
      target={triggerButton}
      onDismiss={onDismiss}
    >
      <div className="bingads-fluctuation-wrapper">
        <div className="fluctuation-upper-content">
          <div className="bold">{dateRange}</div>
          <MetricCounter
            metrics={[{
              ...metric,
              line: 'iconba iconba-Insights',
            }]}
          />
        </div>
        <DescriptionContainer
          description={description}
        />
        <ActionCauseContainer
          i18n={i18n}
          accountsInfo={accountsInfo}
          context={context}
          insightData={insightData}
          isDetailDomExpanded={isDetailDomExpanded}
          onDetailDomShow={() => setIsDetailDomExpanded(!isDetailDomExpanded)}
          onDeepLinkClick={onDeepLinkClick}
        />
        <div className="fluctuation-lower-content">
          <InsightAction
            deps={{ i18n: legacyI18n }}
            onSubmit={onFeedbackSubmit}
            activeSubPanel={currentSubPanel}
            onCurrentSubPanelChange={subPanel => setCurrentSubPanel(subPanel)}
            subPanels={[ActionSubPanel.FeedbackPanel]}
            feedbackOptions={feedbackOptions}
            learnMoreLinkTopic="51126"
          />
        </div>
      </div>
    </CalloutWithAutoPlacement>
  );
};

AuctionInsightPopup.propTypes = {
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
    fluctuation: PropTypes.shape({
      create: PropTypes.func,
    }),
  }).isRequired,
  context: PropTypes.shape({
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.number,
    }),
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number,
      IsInternal: PropTypes.bool,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  insightData: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    metric: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    accountIds: PropTypes.arrayOf(PropTypes.number),
    domainNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  accountsInfo: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number,
    Name: PropTypes.string,
  })),
  onDismiss: PropTypes.func,
  triggerButton: PropTypes.instanceOf(Element).isRequired,
};

AuctionInsightPopup.defaultProps = {
  onDismiss: _.noop,
  accountsInfo: [{}],
};
